import {useState, useEffect, useRef, useCallback} from '@wordpress/element';
import {applyFilters} from '@wordpress/hooks';
import {useApiRequests} from '../../../hooks';
import {isFieldRequired} from '../../../utils';
import {parsePaymentData, mapLineItems, mapShippingOptions} from '../helpers';

export const useApplePaySession = (
    {
        paypal,
        onClose,
        onError,
        billing,
        shipping,
        settings,
        setPaymentData
    }) => {

    const [isReady, setIsReady] = useState(false);
    const [applepayConfig, setApplepayConfig] = useState();
    const sessionRef = useRef(null);
    const currentData = useRef({billing, shipping, paypal, settings});

    const {
        createOrder,
        onShippingChange
    } = useApiRequests({
        isExpress: settings.isExpress,
        paymentMethod: 'ppcp_applepay'
    });

    // Keep current data ref updated
    useEffect(() => {
        currentData.current = {onClose, onError, billing, shipping, paypal, settings};
    }, [onClose, onError, billing, shipping, paypal, settings]);

    useEffect(() => {
        if (paypal) {
            paypal.Applepay().config().then(value => {
                setApplepayConfig(value);
                setIsReady(true);
            });
        }
    }, [paypal]);

    const createBillingContact = useCallback(() => {
        const {billing} = currentData.current;
        const {billingAddress} = billing;
        return {
            givenName: billingAddress.first_name,
            familyName: billingAddress.last_name,
            locality: billingAddress.city,
            addressLines: [
                billingAddress.address_1,
                billingAddress.address_2
            ].filter(Boolean),
            postalCode: billingAddress.postcode,
            administrativeArea: billingAddress.state,
            countryCode: billingAddress.country
        }
    }, []);

    const onCancelSession = useCallback(() => {
        if (currentData.current.onClose) {
            currentData.current.onClose();
        }
    }, []);

    /**
     * Handle merchant validation callback from Apple Pay
     */
    const onValidateMerchant = useCallback(async (event) => {
        try {
            const {paypal, settings} = currentData.current;
            const applePaySession = sessionRef.current;

            const result = await paypal.Applepay().validateMerchant({
                validationUrl: event.validationURL,
                displayName: settings.displayName
            });

            applePaySession.completeMerchantValidation(result.merchantSession);
        } catch (error) {
            console.error('Merchant validation error:', error);
            sessionRef.current?.abort();
            currentData.current.onError(error.message);
        }
    }, []);

    /**
     * Handle payment authorization callback from Apple Pay
     */
    const onPaymentAuthorized = useCallback(async (event) => {
        const applePaySession = sessionRef.current;
        try {
            const {paypal, shipping, settings} = currentData.current;
            // Create the PayPal order
            const orderId = await createOrder(currentData.current);

            const payment = event.payment;

            // Get billing contact from event
            const billingContact = payment.billingContact || createBillingContact();

            // Confirm the order with PayPal
            const response = await paypal.Applepay().confirmOrder({
                orderId,
                token: event.payment.token,
                billingContact
            });

            if (response.approveApplePayPayment.status === 'APPROVED') {
                applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
                if (settings.isExpress) {
                    setPaymentData({
                        orderId,
                        token: payment.token,
                        ...parsePaymentData(payment)
                    }, true);
                } else {
                    setPaymentData({
                        orderId,
                        token: payment.token
                    }, false);
                }
            } else {
                applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
            }
        } catch (error) {
            console.error('Payment authorization error:', error);
            applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
            currentData.current.onError(error.message);
        }
    }, [
        createOrder,
        setPaymentData,
        createBillingContact
    ]);

    /**
     * Handle shipping method selection callback from Apple Pay
     */
    const onShippingMethodSelected = useCallback(async (event) => {
        try {
            const {settings} = currentData.current;
            const applePaySession = sessionRef.current;
            const {identifier = null} = event.shippingMethod;

            const response = await onShippingChange({
                address: null,
                shipping_method: identifier
            }, currentData.current);

            applePaySession.completeShippingMethodSelection(applyFilters('applepay_blocks_complete_shipping_method_selection', {
                newLineItems: mapLineItems(response.lineItems),
                newTotal: {
                    label: settings.i18n.total,
                    type: 'final',
                    amount: response.total
                }
            }, response));
        } catch (error) {
            console.error('Shipping method selection error:', error);
            sessionRef.current?.abort();
            currentData.current.onError(error.message);
        }
    }, [onShippingChange]);

    /**
     * Handle shipping contact selection callback from Apple Pay
     */
    const onShippingContactSelected = useCallback(async (event) => {
        const applePaySession = sessionRef.current;
        try {
            const {settings} = currentData.current;
            const {shippingContact} = event;

            // Format shipping address for WooCommerce
            const address = {
                postcode: shippingContact.postalCode,
                city: shippingContact.locality,
                state: shippingContact.administrativeArea?.toUpperCase(),
                country: shippingContact.countryCode?.toUpperCase()
            };

            const response = await onShippingChange({
                address,
                shipping_method: null
            }, currentData.current);

            applePaySession.completeShippingContactSelection(applyFilters('applepay_blocks_complete_shipping_contact_selection', {
                newLineItems: mapLineItems(response.lineItems),
                newShippingMethods: mapShippingOptions(response.shippingOptions, response.selectedShippingMethod),
                newTotal: {
                    label: settings.i18n.total,
                    type: 'final',
                    amount: response.total
                }
            }, response));
        } catch (error) {
            applePaySession.completeShippingContactSelection(applyFilters('applepay_blocks_complete_shipping_contact_selection', {
                newTotal: {
                    label: settings.i18n.total,
                    type: 'final',
                    amount: settings.total
                },
                errors: [
                    new ApplePayError('addressUnserviceable')
                ]
            }, settings));
        }
    }, [onShippingChange]);

    const createSession = useCallback(async () => {
        try {
            const {
                billing,
                shipping,
                settings
            } = currentData.current;
            const {currency} = billing;
            const {needsShipping} = shipping;
            const {isExpress} = settings;

            // Build payment request
            const shouldRequestShipping = needsShipping && isExpress;

            const paymentRequest = {
                countryCode: applepayConfig.countryCode,
                merchantCapabilities: applepayConfig.merchantCapabilities,
                supportedNetworks: applepayConfig.supportedNetworks,
                currencyCode: currency.code,
                total: {
                    label: settings.i18n?.total || 'Total',
                    type: 'final',
                    amount: settings.total
                },
                lineItems: mapLineItems(settings.lineItems || []),
                requiredBillingContactFields: ['postalAddress'],
                requiredShippingContactFields: []
            };

            // Add billing/shipping contact fields if express
            if (isExpress) {
                if (isFieldRequired('phone', 'billing') || isFieldRequired('phone', 'shipping')) {
                    paymentRequest.requiredShippingContactFields.push('phone');
                }
                if (isFieldRequired('email', 'shipping') || isFieldRequired('email', 'billing')) {
                    paymentRequest.requiredShippingContactFields.push('email');
                }
            }

            // Add shipping methods if needed
            if (shouldRequestShipping) {
                paymentRequest.requiredShippingContactFields.push('name', 'postalAddress');
                if (settings.shippingOptions && settings.shippingOptions.length > 0) {
                    paymentRequest.shippingMethods = mapShippingOptions(
                        settings.shippingOptions,
                        settings.selectedShippingMethod
                    );
                }
            }

            // Create the ApplePaySession
            const applePaySession = new ApplePaySession(4, applyFilters('applepay_blocks_payment_request', paymentRequest, settings));

            // Attach callbacks
            applePaySession.oncancel = onCancelSession;
            applePaySession.onvalidatemerchant = onValidateMerchant;
            applePaySession.onpaymentauthorized = onPaymentAuthorized;
            applePaySession.onshippingmethodselected = onShippingMethodSelected;
            applePaySession.onshippingcontactselected = onShippingContactSelected;

            sessionRef.current = applePaySession;

            return applePaySession;
        } catch (error) {
            console.error('Failed to initialize Apple Pay session:', error);
            throw error;
        }
    }, [
        paypal,
        applepayConfig,
        onCancelSession,
        onValidateMerchant,
        onPaymentAuthorized,
        onShippingMethodSelected,
        onShippingContactSelected
    ]);

    return {
        isReady,
        createSession
    };
};

export default useApplePaySession;